// frontend/src/pages/admin/AdminServiceBookings.js
import React, { useState, useEffect } from 'react';
import { servicesApi } from '../../api/services';
import { serviceOpsApi } from '../../api/serviceOps';
import helpers from '../../utils/helpers';
import { 
    FaSearch, FaFilter, FaFileInvoiceDollar, FaUserCog, FaMapMarkerAlt, 
    FaClipboardCheck, FaTimes, FaCar, FaCheck, FaTools, FaUser, FaRoute, FaTruck, FaEye, FaHistory, FaStore, FaSpinner 
} from 'react-icons/fa';

// --- CONFIGURATION CONSTANT ---
const EXCLUSIVE_PROVIDER_ID = "everything_automotive_workshop";
// ------------------------------

function AdminServiceBookings() {
  const [bookings, setBookings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // --- MODAL STATES ---
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [auditModalOpen, setAuditModalOpen] = useState(false); 
  
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // Audit Data State
  const [auditInvoice, setAuditInvoice] = useState(null);
  const [auditRecord, setAuditRecord] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Form States
  const [quoteItems, setQuoteItems] = useState([{ name: '', quantity: 1, unit_price: '', type: 'part' }]);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState('');
  
  // Completion Form State
  const [completionData, setCompletionData] = useState({
      service_date: new Date().toISOString().split('T')[0],
      mileage_at_service: '',
      faults_fixed: '',
      work_done_description: '',
      labor_cost: '',
      parts_used: [] 
  });
  const [newPart, setNewPart] = useState({ part_name: '', quantity: 1, unit_price: '' });

  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, filterStatus, searchTerm]);

  const fetchData = async () => {
    try {
      const [bookingsData, providersData] = await Promise.all([
        servicesApi.getAllBookingsAdmin(),
        serviceOpsApi.getAllProviders({ is_active: true })
      ]);
      setBookings(bookingsData);
      setProviders(providersData);
    } catch (err) {
      console.error("Error fetching data:", err);
      alert("Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let result = bookings;
    if (filterStatus !== 'all') {
      result = result.filter(b => (b.service_stage || b.status) === filterStatus);
    }
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(b => 
        b.booking_reference.toLowerCase().includes(lowerTerm) ||
        (b.user_email && b.user_email.toLowerCase().includes(lowerTerm)) ||
        (b.user_name && b.user_name.toLowerCase().includes(lowerTerm)) ||
        (b.vehicle_info_snapshot && b.vehicle_info_snapshot.toLowerCase().includes(lowerTerm))
      );
    }
    setFilteredBookings(result);
  };

  // --- AUDIT LOGIC ---
  const handleViewAudit = async (booking) => {
      setSelectedBooking(booking);
      setAuditModalOpen(true);
      setLoadingAudit(true);
      setAuditInvoice(null);
      setAuditRecord(null);

      try {
          const promises = [];
          if (booking.invoice_id) promises.push(servicesApi.getInvoiceDetails(booking.invoice_id));
          if (booking.service_record_id) promises.push(serviceOpsApi.getServiceRecord(booking.service_record_id));
          
          const results = await Promise.allSettled(promises);
          
          if (booking.invoice_id && results[0].status === 'fulfilled') {
              setAuditInvoice(results[0].value);
          }
          
          const recordResult = booking.invoice_id ? results[1] : results[0];
          if (booking.service_record_id && recordResult && recordResult.status === 'fulfilled') {
              setAuditRecord(recordResult.value);
          }

      } catch (err) {
          console.error("Audit fetch error:", err);
      } finally {
          setLoadingAudit(false);
      }
  };

  // --- HANDLERS ---

  const openQuoteModal = (booking) => {
      setSelectedBooking(booking);
      // Default quote item
      setQuoteItems([{ name: 'Initial Diagnosis', quantity: 1, unit_price: '', type: 'diagnosis' }]);
      setQuoteNotes('');
      setQuoteModalOpen(true);
  };

  const openAssignModal = (booking) => {
      setSelectedBooking(booking);
      setSelectedProviderId('');
      setAssignModalOpen(true);
  };

  const openCompleteModal = (booking) => {
      setSelectedBooking(booking);
      setCompletionData({
          service_date: new Date().toISOString().split('T')[0],
          mileage_at_service: '',
          faults_fixed: '',
          work_done_description: '',
          labor_cost: '',
          parts_used: []
      });
      setCompleteModalOpen(true);
  };

  // --- QUOTE FORM HANDLERS ---
  const addQuoteItem = () => {
      setQuoteItems([...quoteItems, { name: '', quantity: 1, unit_price: '', type: 'part' }]);
  };

  const removeQuoteItem = (index) => {
      setQuoteItems(quoteItems.filter((_, i) => i !== index));
  };

  const updateQuoteItem = (index, field, value) => {
      const updated = [...quoteItems];
      updated[index][field] = value;
      setQuoteItems(updated);
  };

  const handleSendQuote = async (e) => {
      e.preventDefault();
      setProcessing(true);
      try {
          const payload = {
              items: quoteItems.map(i => ({
                  ...i,
                  unit_price: parseFloat(i.unit_price),
                  quantity: parseInt(i.quantity)
              })),
              notes: quoteNotes
          };
          await serviceOpsApi.createQuote(selectedBooking.id, payload);
          alert("Quote sent to customer for approval.");
          setQuoteModalOpen(false);
          fetchData();
      } catch (err) {
          alert("Failed to send quote.");
      } finally {
          setProcessing(false);
      }
  };

  const handleAssignProvider = async (e) => {
      e.preventDefault();
      if (!selectedProviderId) return alert("Please select a provider.");
      setProcessing(true);
      try {
          await serviceOpsApi.assignProvider(selectedBooking.id, selectedProviderId);
          alert("Provider assigned successfully!");
          setAssignModalOpen(false);
          fetchData();
      } catch (err) {
          alert("Failed to assign provider.");
      } finally {
          setProcessing(false);
      }
  };

  // --- COMPLETION FORM HANDLERS ---
  const addPart = () => {
      if (!newPart.part_name || !newPart.unit_price) return;
      setCompletionData(prev => ({
          ...prev,
          parts_used: [...prev.parts_used, { ...newPart, unit_price: parseFloat(newPart.unit_price) }]
      }));
      setNewPart({ part_name: '', quantity: 1, unit_price: '' });
  };

  const removePart = (index) => {
      setCompletionData(prev => ({
          ...prev,
          parts_used: prev.parts_used.filter((_, i) => i !== index)
      }));
  };

  const handleCompleteJob = async (e) => {
      e.preventDefault();
      setProcessing(true);
      
      const partsTotal = completionData.parts_used.reduce((sum, p) => sum + (p.unit_price * p.quantity), 0);
      const totalCost = parseFloat(completionData.labor_cost) + partsTotal;

      const payload = {
          vehicle_id: selectedBooking.vehicle_id,
          provider_id: selectedBooking.assigned_provider_id || 'admin', 
          service_date: completionData.service_date,
          mileage_at_service: parseInt(completionData.mileage_at_service),
          faults_fixed: completionData.faults_fixed,
          work_done_description: completionData.work_done_description,
          labor_cost: parseFloat(completionData.labor_cost),
          parts_used: completionData.parts_used,
          total_cost: totalCost
      };

      try {
          await serviceOpsApi.completeJob(selectedBooking.id, payload);
          alert("Job Completed! Receipt sent to customer.");
          setCompleteModalOpen(false);
          fetchData();
      } catch (err) {
          console.error(err);
          alert("Failed to complete job.");
      } finally {
          setProcessing(false);
      }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    if (!window.confirm(`Change status to ${newStatus.toUpperCase()}?`)) return;
    try {
      await servicesApi.updateBookingStatus(id, newStatus);
      fetchData();
    } catch (err) {
      alert("Failed to update status.");
    }
  };

  const getStageBadge = (stage) => {
    const styles = {
      pending_review: "bg-yellow-100 text-yellow-800",
      quote_sent: "bg-blue-100 text-blue-800",
      quote_rejected: "bg-red-100 text-red-800",
      work_in_progress: "bg-purple-100 text-purple-800",
      awaiting_payment: "bg-orange-100 text-orange-800",
      payment_confirmed: "bg-blue-100 text-blue-800",
      scheduled: "bg-gray-100 text-gray-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    const label = stage ? stage.replace(/_/g, ' ') : 'Unknown';
    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase ${styles[stage] || "bg-gray-100"}`}>
        {label}
      </span>
    );
  };

  const getProviderName = (id) => {
      if (id === EXCLUSIVE_PROVIDER_ID) return "EA Workshop (Internal)";
      const provider = providers.find(p => p.id === id);
      return provider ? provider.name : 'Unassigned';
  };

  if (loading) return <div className="p-8 text-center">Loading bookings...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Service Operations</h1>

      {/* FILTERS */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <FaFilter className="text-gray-400" />
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-primary"
          >
            <option value="all">All Stages</option>
            <option value="pending_review">Pending Review</option>
            <option value="scheduled">Scheduled</option>
            <option value="quote_sent">Quote Sent</option>
            <option value="work_in_progress">Work In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="relative w-full md:w-64">
          <input 
            type="text" 
            placeholder="Search Ref, Name..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-primary"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b border-gray-200">
                <th className="p-4">Reference</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Service Details</th>
                <th className="p-4">Stage</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredBookings.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-500">No bookings found.</td>
                </tr>
              ) : (
                filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <div className="font-mono text-sm font-bold text-gray-700">{booking.booking_reference}</div>
                      <div className="text-xs text-gray-500">{new Date(booking.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="p-4 text-sm">
                      <div className="font-bold text-gray-800 flex items-center gap-2">
                        <FaUser className="text-gray-400 text-xs" />
                        {booking.user_name}
                      </div>
                      <div className="text-xs text-gray-500">{booking.user_email}</div>
                      <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                         <FaCar /> {booking.vehicle_info_snapshot}
                      </div>
                    </td>
                    <td className="p-4 text-sm">
                      <div className="capitalize font-medium text-primary">
                        {booking.service_type.replace('_', ' ')}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-[200px]" title={booking.description}>
                        {booking.description}
                      </div>
                      
                      {/* --- LOGISTICS DISPLAY --- */}
                      {(booking.service_type === 'home_service' || booking.service_type === 'roadside_assistance') && (
                          <div className="mt-2 bg-blue-50 p-2 rounded border border-blue-100 text-xs">
                              <div className="flex items-center gap-1 text-blue-800 font-bold mb-1">
                                  <FaMapMarkerAlt /> {booking.address}
                              </div>
                              {booking.distance_km ? (
                                  <div className="flex flex-col gap-1 text-gray-600">
                                      <span className="flex items-center gap-1"><FaRoute /> {booking.distance_km} km</span>
                                      <span className="font-bold text-green-600">Fee: {helpers.formatCurrency(booking.travel_fee, 'NGN')}</span>
                                      {booking.requires_towing && (
                                          <span className="flex items-center gap-1 text-red-600 font-bold bg-red-100 px-1 rounded w-fit">
                                              <FaTruck /> TOWING REQUIRED
                                          </span>
                                      )}
                                  </div>
                              ) : (
                                  <span className="text-orange-500 italic">Distance pending...</span>
                              )}
                          </div>
                      )}
                    </td>
                    <td className="p-4">
                      {getStageBadge(booking.service_stage || booking.status)}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        
                        {/* AUDIT BUTTON (ALWAYS VISIBLE) */}
                        <button 
                            onClick={() => handleViewAudit(booking)}
                            className="bg-gray-100 text-gray-600 p-2 rounded hover:bg-gray-200"
                            title="View Audit Trail / Details"
                        >
                            <FaHistory />
                        </button>

                        {/* 1. CREATE QUOTE (Pending, Scheduled, or Rejected) */}
                        {['pending_review', 'pending', 'scheduled', 'quote_rejected'].includes(booking.service_stage || booking.status) && (
                            <button 
                                onClick={() => openQuoteModal(booking)}
                                className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-xs font-bold flex items-center gap-1 hover:bg-blue-200"
                            >
                                <FaFileInvoiceDollar /> Quote
                            </button>
                        )}

                        {/* 2. ASSIGN PROVIDER */}
                        {['pending_review', 'scheduled', 'quote_sent', 'quote_rejected'].includes(booking.service_stage) && (
                            <button 
                                onClick={() => openAssignModal(booking)}
                                className="bg-purple-100 text-purple-700 px-3 py-1 rounded text-xs font-bold flex items-center gap-1 hover:bg-purple-200"
                            >
                                <FaUserCog /> {booking.assigned_provider_id ? 'Reassign' : 'Assign'}
                            </button>
                        )}

                        {/* 3. COMPLETE JOB (Only if Work In Progress) */}
                        {booking.service_stage === 'work_in_progress' && (
                            <button 
                                onClick={() => openCompleteModal(booking)}
                                className="bg-green-100 text-green-700 px-3 py-1 rounded text-xs font-bold flex items-center gap-1 hover:bg-green-200"
                            >
                                <FaClipboardCheck /> Complete
                            </button>
                        )}

                        {/* 4. CANCEL BUTTON */}
                        {booking.service_stage !== 'completed' && booking.service_stage !== 'cancelled' && (
                            <button 
                                onClick={() => handleStatusUpdate(booking.id, 'cancelled')}
                                className="text-red-400 hover:text-red-600 p-1"
                                title="Cancel Booking"
                            >
                                <FaTimes />
                            </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- AUDIT MODAL --- */}
      {auditModalOpen && selectedBooking && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b bg-gray-50">
                      <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                          <FaHistory /> Audit Trail: {selectedBooking.booking_reference}
                      </h3>
                      <button onClick={() => setAuditModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <div className="p-6 overflow-y-auto space-y-6">
                      {loadingAudit ? (
                          <div className="text-center py-10"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Details...</div>
                      ) : (
                          <>
                              {/* 1. REQUEST DETAILS */}
                              <div className="border rounded-lg p-4">
                                  <h4 className="font-bold text-gray-700 mb-2 border-b pb-1">1. Customer Request</h4>
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                      <div><span className="text-gray-500">Customer:</span> {selectedBooking.user_name}</div>
                                      <div><span className="text-gray-500">Vehicle:</span> {selectedBooking.vehicle_info_snapshot}</div>
                                      <div className="col-span-2"><span className="text-gray-500">Issue:</span> {selectedBooking.description}</div>
                                      <div><span className="text-gray-500">Date:</span> {selectedBooking.preferred_date}</div>
                                      <div><span className="text-gray-500">Type:</span> {selectedBooking.service_type}</div>
                                  </div>
                              </div>

                              {/* 2. QUOTE / INVOICE */}
                              <div className="border rounded-lg p-4 bg-blue-50 border-blue-100">
                                  <h4 className="font-bold text-blue-800 mb-2 border-b border-blue-200 pb-1">2. Provider Quote / Invoice</h4>
                                  {auditInvoice ? (
                                      <div className="text-sm">
                                          <div className="flex justify-between mb-2">
                                              <span><strong>Status:</strong> <span className={`uppercase font-bold ${auditInvoice.status === 'paid' ? 'text-green-600' : 'text-red-600'}`}>{auditInvoice.status}</span></span>
                                              <span><strong>Amount:</strong> {helpers.formatCurrency(auditInvoice.amount, 'NGN')}</span>
                                          </div>
                                          <div className="bg-white p-3 rounded border border-blue-100">
                                              <p className="text-xs text-gray-500 uppercase font-bold mb-1">Line Items / Description:</p>
                                              <p className="whitespace-pre-wrap">{auditInvoice.description}</p>
                                          </div>
                                          {auditInvoice.payment_ref && (
                                              <p className="mt-2 text-xs text-gray-500">Payment Ref: {auditInvoice.payment_ref}</p>
                                          )}
                                      </div>
                                  ) : (
                                      <p className="text-sm text-gray-500 italic">No invoice generated yet.</p>
                                  )}
                              </div>

                              {/* 3. COMPLETION RECORD */}
                              <div className="border rounded-lg p-4 bg-green-50 border-green-100">
                                  <h4 className="font-bold text-green-800 mb-2 border-b border-green-200 pb-1">3. Completion Report</h4>
                                  {auditRecord ? (
                                      <div className="text-sm">
                                          <div className="grid grid-cols-2 gap-4 mb-3">
                                              <div><span className="text-gray-500">Date:</span> {auditRecord.service_date}</div>
                                              <div><span className="text-gray-500">Mileage:</span> {auditRecord.mileage_at_service} km</div>
                                          </div>
                                          <div className="mb-3">
                                              <p className="font-bold text-gray-700">Faults Fixed:</p>
                                              <p>{auditRecord.faults_fixed}</p>
                                          </div>
                                          <div className="mb-3">
                                              <p className="font-bold text-gray-700">Work Done:</p>
                                              <p>{auditRecord.work_done_description}</p>
                                          </div>
                                          {auditRecord.parts_used && auditRecord.parts_used.length > 0 && (
                                              <div className="bg-white p-3 rounded border border-green-100">
                                                  <p className="font-bold text-gray-700 mb-1">Parts Used:</p>
                                                  <ul className="list-disc list-inside">
                                                      {auditRecord.parts_used.map((p, i) => (
                                                          <li key={i}>{p.quantity}x {p.part_name} - {helpers.formatCurrency(p.unit_price, 'NGN')}</li>
                                                      ))}
                                                  </ul>
                                              </div>
                                          )}
                                          <div className="mt-3 text-right font-bold text-lg text-green-800">
                                              Final Total: {helpers.formatCurrency(auditRecord.total_cost, 'NGN')}
                                          </div>
                                      </div>
                                  ) : (
                                      <p className="text-sm text-gray-500 italic">Job not yet marked as complete.</p>
                                  )}
                              </div>
                              
                              {/* 4. ASSIGNMENT DETAILS */}
                              <div className="border rounded-lg p-4 bg-purple-50 border-purple-100">
                                  <h4 className="font-bold text-purple-800 mb-2 border-b border-purple-200 pb-1">4. Assignment Details</h4>
                                  <div className="text-sm">
                                      <p><strong>Assigned Provider:</strong> {getProviderName(selectedBooking.assigned_provider_id)}</p>
                                      <p><strong>Assigned At:</strong> {selectedBooking.assigned_at ? new Date(selectedBooking.assigned_at).toLocaleString() : 'N/A'}</p>
                                  </div>
                              </div>
                          </>
                      )}
                  </div>
                  <div className="p-4 border-t bg-gray-50 text-right">
                      <button onClick={() => setAuditModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-bold">Close Audit</button>
                  </div>
              </div>
          </div>
      )}

      {/* --- QUOTE MODAL --- */}
      {quoteModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b">
                      <h3 className="text-xl font-bold">Create Service Quote</h3>
                      <button onClick={() => setQuoteModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <form onSubmit={handleSendQuote} className="overflow-y-auto p-6 space-y-4">
                      <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                          List all parts and labor required. The customer must approve this total before you start work.
                      </div>

                      {quoteItems.map((item, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                              <input 
                                  placeholder="Item Description" 
                                  value={item.name} 
                                  onChange={(e) => updateQuoteItem(idx, 'name', e.target.value)} 
                                  className="flex-grow p-2 border rounded"
                                  required
                              />
                              <input 
                                  type="number" 
                                  placeholder="Qty" 
                                  value={item.quantity} 
                                  onChange={(e) => updateQuoteItem(idx, 'quantity', e.target.value)} 
                                  className="w-16 p-2 border rounded"
                                  min="1"
                                  required
                              />
                              <input 
                                  type="number" 
                                  placeholder="Price" 
                                  value={item.unit_price} 
                                  onChange={(e) => updateQuoteItem(idx, 'unit_price', e.target.value)} 
                                  className="w-24 p-2 border rounded"
                                  required
                              />
                              <select 
                                  value={item.type} 
                                  onChange={(e) => updateQuoteItem(idx, 'type', e.target.value)} 
                                  className="p-2 border rounded bg-white"
                              >
                                  <option value="part">Part</option>
                                  <option value="labor">Labor</option>
                                  <option disabled>──────────</option>
                                  <option value="towing_fee">Towing Fee</option>
                                  <option value="call_out">Call-out</option>
                                  <option value="travel_fee">Travel Fee</option>
                                  <option value="pickup_delivery">Pickup & Delivery Fee</option>
                                  <option disabled>──────────</option>
                                  <option value="environmental">Environmental</option>
                                  <option value="disposal_fee">Disposal Fee</option>
                                  <option value="shop_supplies">Shop Supplies</option>
                                  <option value="consumables">Consumables</option>
                                  <option value="storage_fee">Storage Fee</option>
                                  <option disabled>──────────</option>
                                  <option value="diagnosis">Diagnosis</option>
                                  <option value="scanning_fee">Scanning Fee</option>
                                  <option value="inspection_fee">Inspection Fee</option>
                                  <option value="machine_shop">Machine Shop</option>
                                  <option value="sublet_work">Sublet Work</option>
                                  <option disabled>──────────</option>
                                  <option value="service_charge">Service Charge</option>
                                  <option value="admin_fee">Admin Fee</option>
                                  <option value="vat_tax">VAT / Tax</option>
                              </select>
                              <button type="button" onClick={() => removeQuoteItem(idx)} className="text-red-500"><FaTimes /></button>
                          </div>
                      ))}
                      
                      <button type="button" onClick={addQuoteItem} className="text-blue-600 font-bold text-sm">+ Add Item</button>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Notes (Optional)</label>
                          <textarea 
                              value={quoteNotes} 
                              onChange={(e) => setQuoteNotes(e.target.value)} 
                              className="w-full p-2 border rounded" 
                              rows="2"
                              placeholder="e.g. Estimate valid for 7 days. Work starts upon approval."
                          ></textarea>
                      </div>

                      <div className="flex justify-between items-center pt-4 border-t">
                          <div className="text-lg font-bold">
                              Total: {helpers.formatCurrency(quoteItems.reduce((sum, i) => sum + (i.unit_price * i.quantity), 0), 'NGN')}
                          </div>
                          <div className="flex gap-2">
                              <button type="button" onClick={() => setQuoteModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                              <button type="submit" disabled={processing} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                                  {processing ? <FaSpinner className="animate-spin" /> : 'Send Quote'}
                              </button>
                          </div>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* --- ASSIGN MODAL --- */}
      {assignModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                  <h3 className="text-lg font-bold mb-4">Assign Service Provider</h3>
                  <form onSubmit={handleAssignProvider}>
                      <div className="mb-4">
                          <label className="block text-sm font-bold text-gray-700 mb-1">Select Provider</label>
                          <select 
                              required 
                              value={selectedProviderId}
                              onChange={(e) => setSelectedProviderId(e.target.value)}
                              className="w-full p-2 border rounded bg-white"
                          >
                              <option value="">-- Choose a Provider --</option>
                              <option value={EXCLUSIVE_PROVIDER_ID}>EA Workshop (Internal)</option>
                              {providers.filter(p => p.id !== EXCLUSIVE_PROVIDER_ID).map(p => (
                                  <option key={p.id} value={p.id}>
                                      {p.name} ({p.type}) - {p.phone}
                                  </option>
                              ))}
                          </select>
                      </div>
                      <div className="flex justify-end gap-2">
                          <button type="button" onClick={() => setAssignModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                          <button type="submit" disabled={processing} className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700">
                              {processing ? 'Assigning...' : 'Confirm Assignment'}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* --- COMPLETE JOB MODAL --- */}
      {completeModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b">
                      <h3 className="text-xl font-bold">Complete Job Report</h3>
                      <button onClick={() => setCompleteModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <form onSubmit={handleCompleteJob} className="overflow-y-auto p-6 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Service Date</label>
                              <input type="date" required value={completionData.service_date} onChange={(e) => setCompletionData({...completionData, service_date: e.target.value})} className="w-full p-2 border rounded" />
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Mileage at Service</label>
                              <input type="number" required value={completionData.mileage_at_service} onChange={(e) => setCompletionData({...completionData, mileage_at_service: e.target.value})} className="w-full p-2 border rounded" />
                          </div>
                      </div>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Faults Fixed</label>
                          <input type="text" required value={completionData.faults_fixed} onChange={(e) => setCompletionData({...completionData, faults_fixed: e.target.value})} className="w-full p-2 border rounded" placeholder="e.g. Worn Brake Pads, Oil Leak" />
                      </div>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Work Done Description</label>
                          <textarea required value={completionData.work_done_description} onChange={(e) => setCompletionData({...completionData, work_done_description: e.target.value})} className="w-full p-2 border rounded h-20" placeholder="Detailed description of work..."></textarea>
                      </div>

                      {/* PARTS USED SECTION */}
                      <div className="bg-gray-50 p-4 rounded border">
                          <label className="block text-xs font-bold text-gray-600 mb-2">Parts Used (Final)</label>
                          {completionData.parts_used.map((part, idx) => (
                              <div key={idx} className="flex justify-between items-center bg-white p-2 mb-2 rounded border text-sm">
                                  <span>{part.quantity}x {part.part_name}</span>
                                  <div className="flex items-center gap-3">
                                      <span>{helpers.formatCurrency(part.unit_price * part.quantity, 'NGN')}</span>
                                      <button type="button" onClick={() => removePart(idx)} className="text-red-500"><FaTimes /></button>
                                  </div>
                              </div>
                          ))}
                          
                          <div className="flex gap-2 mt-2">
                              <input placeholder="Part Name" value={newPart.part_name} onChange={(e) => setNewPart({...newPart, part_name: e.target.value})} className="flex-grow p-2 border rounded text-sm" />
                              <input type="number" placeholder="Qty" value={newPart.quantity} onChange={(e) => setNewPart({...newPart, quantity: parseInt(e.target.value)})} className="w-16 p-2 border rounded text-sm" />
                              <input type="number" placeholder="Price" value={newPart.unit_price} onChange={(e) => setNewPart({...newPart, unit_price: e.target.value})} className="w-24 p-2 border rounded text-sm" />
                              <button type="button" onClick={addPart} className="bg-blue-600 text-white px-3 rounded text-sm">Add</button>
                          </div>
                      </div>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Labor Cost (NGN)</label>
                          <input type="number" required value={completionData.labor_cost} onChange={(e) => setCompletionData({...completionData, labor_cost: e.target.value})} className="w-full p-2 border rounded" />
                      </div>

                      <div className="flex justify-end gap-3 pt-4 border-t">
                          <button type="button" onClick={() => setCompleteModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                          <button type="submit" disabled={processing} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                              {processing ? 'Processing...' : 'Complete Job'}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

    </div>
  );
}

export default AdminServiceBookings;